interface Point {
  x: number;
  y: number;
}

/**
 * Parses attribute `d` of `<path>` and returns array of x, y pairs
 * of the points that this path goes through
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths
 */
export const getPathKeyPoints = (path: string): Point[] => {
  if (/a/i.test(path)) {
    throw new Error(
      `Path attributes "a", "A" currently not supported. Got: ${path}`,
    );
  }

  const parts = path
    // it's allowed to drop separator if number is negative
    // Restore separators back
    .replace(/(\d)(-\d)/g, '$1,$2')
    // we don't care about z operator which just closes the path to the first point
    .replace(/z/i, '')
    .matchAll(/([a-z])([\d., -]+)/gi);

  const points: Point[] = [];
  let prevPoint: Point = { x: 0, y: 0 };

  for (const [, operator, args] of parts) {
    const coords = args.trim().split(/[, ]+/);

    // for "v" and "h" (vertical/horizontal line) one of coords is kept without
    // changes so use prevPoint here
    let y = operator.toLowerCase() === 'h' ? prevPoint.y : Number(coords.pop());
    let x = operator.toLowerCase() === 'v' ? prevPoint.x : Number(coords.pop());

    if (Number.isNaN(x) || Number.isNaN(y)) {
      throw new Error(`Failed to parse values (${x}, ${y}): ${args}`);
    }

    if (['l', 'c', 's', 'q', 't', 'v', 'h'].includes(operator)) {
      // this is relative coords, so we need to use previous x, y
      // for v/h we should change only one of coords

      if (operator !== 'v') {
        x += prevPoint.x;
      }

      if (operator !== 'h') {
        y += prevPoint.y;
      }
    }

    points.push({ x, y });

    prevPoint = { x, y };
  }

  return points;
};
